import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import { renderTitle } from "@/Layouts/components/Tiers/helpers";
import TierColumns from "@/Layouts/components/Tiers/TierColumns";
import TierColumnsItem from "@/Layouts/components/Tiers/TierColumnsItem";
import { Grid, Stack } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { EvaluationTypeOptions } from "./EvaluationTypeOptions";
import MatrixColumnCell from "./MatrixColumnCell";
import { IBonus, IBonusTier } from "./types";

interface MatrixTierColumnsProps {
	title: string;
	titleTooltip?: string;
	readOnly?: boolean;
	tiers: IBonusTier[];
	disableAddColumn?: boolean;
	errors?: any[];
	minimumColumns?: number;
	columnLabel?: string;
	position?: string;
	evaluationType?: string;
	handleAddColumn: () => void;
	handleDeleteColumn: () => void;
	handleUpdateValue: (...args: any[]) => void;
	handleUpdateInputType: (...args: any[]) => void;
	onEvaluationTypeChange?: (evaluationType: string) => void;
}

const evaluationTypeOptions = Object.entries(EvaluationTypeOptions).map(([value, { label }]) => ({ value, label }));

const MatrixTierColumns: FC<MatrixTierColumnsProps> = (props) => {
	const {
		title,
		titleTooltip,
		columnLabel,
		readOnly = false,
		tiers,
		disableAddColumn = false,
		errors = [],
		minimumColumns = 1,
		position,
		evaluationType = "ACTIVATION",
		handleAddColumn,
		handleDeleteColumn,
		handleUpdateValue,
		handleUpdateInputType,
		onEvaluationTypeChange,
	} = props;

	const [selectedEvaluationType, setSelectedEvaluationType] = useState<any>();

	useEffect(() => {
		setSelectedEvaluationType(evaluationTypeOptions.find((opt) => opt.value === evaluationType));
	}, [evaluationType]);

	const handleEvaluationTypeChange = (selected: any) => {
		setSelectedEvaluationType(selected);
		onEvaluationTypeChange?.(selected?.value);
	};

	const customTitleComponent = (
		<Stack direction="column" spacing="16px" alignItems="center">
			{renderTitle(
				{
					className: "tiered-title tiered-title-adjustment",
					sx: { display: "flex", alignItems: "flex-start" },
					icon: {
						present: !!titleTooltip,
						text: titleTooltip,
					},
				},
				title
			)}
			<SmartSelector
				showBy="label"
				sortBy="label"
				options={evaluationTypeOptions}
				selected={selectedEvaluationType}
				selectBy="value"
				inputProps={{
					placeholder: "Evaluation Type",
					classes:
						"[&_.MuiInputBase-root]:!w-[184px] [&_.MuiInputBase-root]:!h-[30px] [&_.MuiInputBase-root]:!min-h-[30px] [&_.MuiInputBase-root]:!py-0",
				}}
				popperProps={{
					className: "!z-[1400]",
				}}
				onChangeAction={handleEvaluationTypeChange}
				disabled={readOnly}
			/>
		</Stack>
	);

	return (
		<Grid item xs={12} mt={2} ml={4}>
			<TierColumns
				useComponent={true}
				component={customTitleComponent}
				titleContainerProps={{
					justifyContent: "flex-start",
					alignItems: "end",
				}}
				disabledDelete={readOnly || tiers[0]?.bonuses?.length <= minimumColumns}
				disabledAdd={disableAddColumn}
				onClickDelete={handleDeleteColumn}
				onClickAdd={handleAddColumn}
			>
				<Stack direction={"row"} position={"relative"}>
					{tiers[0]?.bonuses?.map((bonus: IBonus, index: number) => (
						<TierColumnsItem key={`tier_col_${index}`} index={index} title={columnLabel} sxColumns={{ minHeight: "100px" }}>
							<MatrixColumnCell
								index={index}
								item={bonus}
								errors={errors}
								position={position}
								readOnly={readOnly}
								onActionUpdate={handleUpdateValue}
								onChangeType={handleUpdateInputType}
							/>
						</TierColumnsItem>
					))}
				</Stack>
			</TierColumns>
		</Grid>
	);
};

export default MatrixTierColumns;
